/* eslint-disable react/no-unescaped-entities */

import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Loader2, LogOut, Mail, Phone, MapPin, Calendar, ArrowLeft, RefreshCw, Search } from "lucide-react";
import { fetchLeads, fetchStats, verifyAdmin } from "@/lib/api";
import { LOGO_URL } from "@/lib/content";
import { toast } from "sonner";

const TOKEN_KEY = "hp_admin_token";

function LoginScreen({ onAuth }) {
  const [token, setToken] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await verifyAdmin(token);
      localStorage.setItem(TOKEN_KEY, token);
      onAuth(token);
      toast.success("Connecté");
    } catch {
      toast.error("Token invalide");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#050505] px-6">
      <form onSubmit={handleSubmit} className="w-full max-w-md bg-[#0a0a0a] border border-zinc-800 p-8" data-testid="admin-login-form">
        <div className="flex items-center gap-3 mb-8">
          <img src={LOGO_URL} alt="" className="h-10 w-10" />
          <div>
            <div className="font-display text-2xl text-white">HORSEPOWER</div>
            <div className="font-mono text-[10px] text-[#FFD60A] tracking-widest">ADMIN PANEL</div>
          </div>
        </div>
        <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">Token administrateur</label>
        <input
          type="password"
          value={token}
          onChange={(e) => setToken(e.target.value)}
          placeholder="••••••••••••••••"
          className="w-full bg-black border border-zinc-800 text-white px-4 py-3 focus:border-[#FFD60A] focus:outline-none focus:ring-1 focus:ring-[#FFD60A] font-mono"
          required
          data-testid="admin-token-input"
        />
        <button
          type="submit"
          disabled={loading}
          className="mt-6 w-full flex items-center justify-center gap-3 py-3 bg-[#FFD60A] text-black font-bold uppercase tracking-wider hover:bg-yellow-300 transition-all disabled:opacity-50"
          data-testid="admin-login-submit"
        >
          {loading ? <Loader2 size={18} className="animate-spin" /> : "Se connecter"}
        </button>
        <Link to="/" className="mt-6 flex items-center gap-2 text-xs text-zinc-500 hover:text-white">
          <ArrowLeft size={12} /> Retour au site
        </Link>
      </form>
    </div>
  );
}

const SOURCE_LABELS = {
  contact_form: "Formulaire contact",
  brochure: "Brochure",
  call_booking: "Appel découverte",
  session: "Session présentielle",
};

const SOURCE_COLORS = {
  contact_form: "border-zinc-700 text-zinc-300",
  brochure: "border-blue-500/40 text-blue-400",
  call_booking: "border-[#FFD60A]/40 text-[#FFD60A]",
  session: "border-emerald-500/40 text-emerald-400",
};

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    (async () => {
      try {
        await verifyAdmin(token);
        setAuthed(true);
        await refresh(token);
      } catch {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const refresh = async (tk = token) => {
    setLoading(true);
    try {
      const [l, s] = await Promise.all([fetchLeads(tk), fetchStats(tk)]);
      setLeads(l);
      setStats(s);
    } catch {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  const handleAuth = async (tk) => {
    setToken(tk);
    setAuthed(true);
    await refresh(tk);
  };

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setAuthed(false);
    setLeads([]);
  };

  if (loading && !authed) {
    return <div className="min-h-screen flex items-center justify-center bg-[#050505]"><Loader2 className="animate-spin text-[#FFD60A]" /></div>;
  }
  if (!authed) return <LoginScreen onAuth={handleAuth} />;

  const filtered = leads.filter((l) => {
    if (filter !== "all" && l.source !== filter) return false;
    if (!query) return true;
    const q = query.toLowerCase();
    return [l.nom, l.prenom, l.email, l.ville, l.telephone, l.projet]
      .some((v) => (v || "").toLowerCase().includes(q));
  });

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Top bar */}
      <header className="border-b border-zinc-900 bg-black/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="" className="h-8 w-8" />
            <div>
              <div className="font-display text-lg text-white leading-none">HORSEPOWER</div>
              <div className="font-mono text-[9px] text-[#FFD60A] tracking-widest">ADMIN · LEADS</div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <button onClick={() => refresh()} className="p-2 border border-zinc-800 hover:border-[#FFD60A] text-zinc-400 hover:text-[#FFD60A] transition-colors" data-testid="admin-refresh">
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            </button>
            <button onClick={handleLogout} className="flex items-center gap-2 px-3 py-2 border border-zinc-800 text-xs uppercase tracking-widest text-zinc-400 hover:text-white hover:border-white transition-colors" data-testid="admin-logout">
              <LogOut size={12} /> Déconnexion
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-12 py-10">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-zinc-800 border border-zinc-800 mb-10">
            <div className="bg-[#0a0a0a] p-6">
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Total leads</div>
              <div className="font-display text-4xl text-[#FFD60A] mt-2">{stats.total}</div>
            </div>
            {Object.entries(stats.by_source).map(([k, v]) => (
              <div key={k} className="bg-[#0a0a0a] p-6">
                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{SOURCE_LABELS[k]}</div>
                <div className="font-display text-4xl text-white mt-2">{v}</div>
              </div>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-3 md:items-center mb-6">
          <div className="flex items-center gap-2 flex-wrap">
            {["all", "contact_form", "call_booking", "brochure", "session"].map((s) => (
              <button
                key={s}
                onClick={() => setFilter(s)}
                className={`px-3 py-1.5 text-xs uppercase tracking-widest border transition-all ${
                  filter === s ? "border-[#FFD60A] bg-[#FFD60A]/10 text-[#FFD60A]" : "border-zinc-800 text-zinc-400 hover:border-zinc-600"
                }`}
                data-testid={`filter-${s}`}
              >
                {s === "all" ? "Tous" : SOURCE_LABELS[s]}
              </button>
            ))}
          </div>
          <div className="relative md:ml-auto md:w-72">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Rechercher…"
              className="w-full bg-black border border-zinc-800 text-white pl-9 pr-4 py-2 text-sm focus:border-[#FFD60A] focus:outline-none"
              data-testid="admin-search"
            />
          </div>
        </div>

        {/* Table */}
        <div className="border border-zinc-800 overflow-x-auto" data-testid="admin-leads-table">
          <table className="w-full text-sm">
            <thead className="bg-black border-b border-zinc-800">
              <tr className="text-left text-zinc-500 font-mono text-[10px] uppercase tracking-widest">
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Contact</th>
                <th className="px-4 py-3 hidden md:table-cell">Ville</th>
                <th className="px-4 py-3 hidden lg:table-cell">Source</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-16 text-center text-zinc-500">Aucun lead</td></tr>
              )}
              {filtered.map((l) => (
                <tr key={l.id} className="border-b border-zinc-900 hover:bg-[#0a0a0a] transition-colors" data-testid={`lead-row-${l.id}`}>
                  <td className="px-4 py-4 font-mono text-xs text-zinc-400 whitespace-nowrap">
                    {new Date(l.created_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}
                  </td>
                  <td className="px-4 py-4">
                    <div className="font-semibold text-white">{l.prenom} {l.nom}</div>
                    <div className="text-xs text-zinc-500 flex items-center gap-3 mt-1">
                      <span className="flex items-center gap-1"><Mail size={11} />{l.email}</span>
                      <span className="hidden sm:flex items-center gap-1"><Phone size={11} />{l.telephone}</span>
                    </div>
                  </td>
                  <td className="px-4 py-4 hidden md:table-cell text-zinc-300">{l.ville}</td>
                  <td className="px-4 py-4 hidden lg:table-cell">
                    <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-widest border font-mono ${SOURCE_COLORS[l.source] || ""}`}>
                      {SOURCE_LABELS[l.source] || l.source}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-right">
                    <button
                      onClick={() => setSelected(l)}
                      className="px-3 py-1.5 text-xs uppercase tracking-widest border border-zinc-800 hover:border-[#FFD60A] hover:text-[#FFD60A] text-zinc-400 transition-all"
                      data-testid={`view-lead-${l.id}`}
                    >
                      Voir
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>

      {/* Detail drawer */}
      {selected && (
        <div className="fixed inset-0 z-50 flex justify-end" onClick={() => setSelected(null)}>
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
          <div className="relative w-full max-w-md bg-[#0a0a0a] border-l border-[#FFD60A]/30 h-full overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-zinc-800">
              <button onClick={() => setSelected(null)} className="text-zinc-500 hover:text-white text-xs uppercase tracking-widest flex items-center gap-2">
                <ArrowLeft size={12} /> Retour
              </button>
              <div className="mt-4">
                <div className="font-mono text-[10px] text-[#FFD60A] tracking-widest uppercase">{SOURCE_LABELS[selected.source]}</div>
                <h3 className="font-display text-3xl text-white uppercase mt-1 leading-none">{selected.prenom} {selected.nom}</h3>
                {selected.session_label && <div className="mt-2 text-xs text-[#FFD60A]">{selected.session_label}</div>}
              </div>
            </div>
            <div className="p-6 space-y-5 text-sm">
              <div className="flex items-start gap-3">
                <Mail size={14} className="text-[#FFD60A] mt-1" />
                <div>
                  <div className="text-zinc-500 text-xs uppercase tracking-widest">Email</div>
                  <a href={`mailto:${selected.email}`} className="text-white hover:text-[#FFD60A]">{selected.email}</a>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Phone size={14} className="text-[#FFD60A] mt-1" />
                <div>
                  <div className="text-zinc-500 text-xs uppercase tracking-widest">Téléphone</div>
                  <a href={`tel:${selected.telephone}`} className="text-white hover:text-[#FFD60A]">{selected.telephone}</a>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <MapPin size={14} className="text-[#FFD60A] mt-1" />
                <div>
                  <div className="text-zinc-500 text-xs uppercase tracking-widest">Ville</div>
                  <div className="text-white">{selected.ville}</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Calendar size={14} className="text-[#FFD60A] mt-1" />
                <div>
                  <div className="text-zinc-500 text-xs uppercase tracking-widest">Reçu le</div>
                  <div className="text-white font-mono text-xs">{new Date(selected.created_at).toLocaleString("fr-FR")}</div>
                </div>
              </div>
              <div className="pt-4 border-t border-zinc-800">
                <div className="text-zinc-500 text-xs uppercase tracking-widest mb-2">Projet</div>
                <p className="text-zinc-200 leading-relaxed whitespace-pre-wrap">{selected.projet}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
